import gql from 'graphql-tag';

export const SEARCH_CONTENT_LAYOUTS = gql`
  query SearchContentLayouts($itemTypes: [SearchableTypeEnum], $perPage: Integer!, $page: Integer!, $liveOnly: Bool, $sortStrings: [Str], $filterStrings: [Str], $terms: [Str]) {
    _searchStats(itemTypes: $itemTypes, filterStrings: $filterStrings, terms: $terms, liveOnly: $liveOnly) {
      count
    }

    search(itemTypes: $itemTypes, sortStrings: $sortStrings, filterStrings: $filterStrings, terms: $terms, liveOnly: $liveOnly, perPage: $perPage, page: $page) {
      itemType
      item {
        ... on ContentLayout {
          oid
          id
          name
          body
          description
          active
          type
          templatePath
          templateSyntax
          assignedCount
          latestVersion {
            id
            versionNumber
            createdBy {
              ... on User {
                email
              }
            }
          }
        }
      }
    }
  }
`;

export const CONTENT_LAYOUT_CREATE = gql`
  mutation ContentLayoutCreate($input: ContentLayoutCreateInput!) {
    contentLayoutCreate(input: $input) {
      contentLayout {
        id
        oid
        body
        templatePath
        templateSyntax
      }
    }
  }
`;

export const CONTENT_LAYOUT_EDIT_QUERY = gql`
  query ContentLayoutEditQuery($oid: Integer!) {
    contentLayout(oid: $oid) {
      oid
      id
      name
      body
      description
      active
      type
      templatePath
      templateSyntax
      latestVersion {
        id
        versionNumber
        bodyDiff
        createdBy {
          ... on User {
            email
          }
        }
      }
    }
  }
`;

export const CONTENT_LAYOUT_UPDATE = gql`
  mutation ContentLayoutUpdate($input: ContentLayoutUpdateInput!) {
    contentLayoutUpdate(input: $input) {
      contentLayout {
        id
        oid
        name
        body
        description
        active
        templatePath
        templateSyntax
        latestVersion {
          id
          versionNumber
        }
      }
      success
      errors
    }
  }
`;

export const CONTENT_LAYOUT_VERSIONS = gql`
  query ContentLayoutVersions($oid: Integer!, $page: Integer!, $perPage: Integer!) {
    contentLayout(oid: $oid) {
      oid
      id
      name
      versions(page: $page, perPage: $perPage) {
        id
        versionNumber
        body
        description
        changeNotes
        bodyDiff
        createdAt
        createdBy {
          ... on User {
            id
            email
            firstName
            lastName
          }
        }
      }
      _versionStats {
        count
      }
    }
  }
`;

export const CONTENT_LAYOUT_SELECT_LIST = gql`
  query ContentLayoutSelectList($page: Integer!, $perPage: Integer!) {
    contentLayouts(page: $page, perPage: $perPage) {
      id
      oid
      name
      description
      active
      assignedCount
    }
    _contentLayoutStats {
      count
    }
  }
`;

export const CONTENT_LAYOUT_ASSIGN = gql`
  mutation ContentLayoutAssign($input: ContentLayoutAssignInput!) {
    contentLayoutAssign(input: $input) {
      assignment {
        id
        contentLayout {
          id
          name
        }
      }
      success
      errors
    }
  }
`;

export const CONTENT_LAYOUT_UNASSIGN = gql`
  mutation ContentLayoutUnassign($input: ContentLayoutUnassignInput!) {
    contentLayoutUnassign(input: $input) {
      success
      errors
      unassignedId
    }
  }
`;

/**
 * The layout assignments of a batch of articles, for the article list's bulk
 * actions.
 *
 * An article renders its newest assignment, and assigning always adds a row.
 * Bulk assign reads these first so it only adds a row where the layout is not
 * already the one rendering, and bulk remove needs the assignment ids to
 * unassign.
 */
export const ARTICLE_CONTENT_LAYOUT_ASSIGNMENTS = gql`
  query ArticleContentLayoutAssignments($ids: [IDType], $perPage: Integer!) {
    articles(ids: $ids, perPage: $perPage, page: 1) {
      id
      contentLayoutAssignments {
        id
        oid
        createdAt
        contentLayout {
          id
        }
      }
    }
  }
`;

export const CONTENT_LAYOUT_DELETE = gql`
  mutation ContentLayoutDelete($input: ContentLayoutDeleteInput!) {
    contentLayoutDelete(input: $input) {
      success
      errors
      deletedId
    }
  }
`;

/**
 * The view templates this site renders, and what already overrides them.
 *
 * Read live from jade through the API rather than from a list shipped in this
 * bundle: a client-gem site shadows some 200 of jade's templates, so a browser
 * built from jade's own views would offer a template that is not the one that
 * renders there. `refused` is why a path cannot be overridden, in words meant
 * to be shown.
 */
export const CONTENT_LAYOUT_TEMPLATES = gql`
  query ContentLayoutTemplates($overridableOnly: Bool, $prefix: Str, $search: Str) {
    contentLayoutTemplates(overridableOnly: $overridableOnly, prefix: $prefix, search: $search) {
      customRoot
      counts {
        total
        overridable
        overridablePartials
        overridden
        uncategorized
        custom
        broken
      }
      templates {
        path
        label
        prefix
        origin
        shadowed
        formats
        handlers
        syntax
        partial
        overridable
        refused
        caveat
        custom
        exists
        renderCall
        broken
        overrides {
          oid
          name
          active
        }
        referencedBy {
          oid
          name
          active
        }
      }
    }
  }
`;

/**
 * One template's source, and the syntax it is written in.
 *
 * A template override IS this source: it goes into the editor verbatim and jade
 * compiles it with the same handler that compiles the file. Nothing is
 * converted, so the query asks for nothing but the two things that travel —
 * `source` and `syntax` — plus the provenance the editor shows beside them.
 *
 * The `flags` tree the API still declares is not selected here. It described the
 * site configuration a converter had to fold or refuse before a body could be
 * stored; a raw override evaluates `Site::FOO` itself, on the server.
 */
export const CONTENT_LAYOUT_TEMPLATE_SOURCE = gql`
  query ContentLayoutTemplateSource($path: Str!) {
    contentLayoutTemplateSource(path: $path) {
      path
      label
      origin
      handlers
      syntax
      overridable
      refused
      source
      custom
      exists
      renderCall
      broken
      customPartials
      partials {
        path
        allowed
      }
    }
  }
`;

/**
 * Does this override compile?
 *
 * ActionView compiles a template override the first time it renders, so a stray
 * space in a Slim block is a 500 on every page that template serves. jade holds
 * the handlers, so it answers the same question while the developer is still
 * looking at the source.
 */
export const CONTENT_LAYOUT_TEMPLATE_CHECK = gql`
  query ContentLayoutTemplateCheck($body: Str!, $syntax: Str, $templatePath: Str) {
    contentLayoutTemplateCheck(body: $body, syntax: $syntax, templatePath: $templatePath) {
      ok
      message
      line
      references {
        path
        exists
      }
    }
  }
`;

/**
 * Records a preview can be pointed at.
 *
 * The site picks the most recent record of whatever type the template path
 * implies, which is right for a first look and wrong as soon as you are working
 * on a specific page — a sponsored article, a topic with no children, the one
 * event that has a venue. This is how the editor offers the rest.
 *
 * The admin's own SOLR search rather than a new endpoint on the site: it already
 * indexes every type here, and the preview only needs the type and the oid —
 * jade resolves the record's own URL from those (`canonical_path`).
 *
 * Five types, because those are the ones SearchableTypeEnum carries AND jade's
 * previews controller can resolve (`RECORD_TYPES`). A blog landing page has no
 * searchable type, so it is reached with a custom path instead.
 */
export const SEARCH_PREVIEW_TARGETS = gql`
  query SearchPreviewTargets($itemTypes: [SearchableTypeEnum], $terms: [Str], $perPage: Integer!) {
    search(itemTypes: $itemTypes, terms: $terms, perPage: $perPage, page: 1, liveOnly: false) {
      itemType
      item {
        ... on Article {
          oid
          title
        }
        ... on Page {
          oid
          title
        }
        ... on Taxonomy {
          oid
          title
        }
        ... on Event {
          oid
          title
        }
        ... on Product {
          oid
          title
        }
      }
    }
  }
`;

/**
 * Where to look at an unsaved layout body.
 *
 * The answer is a URL on the site, not markup. jade parks the body under a
 * token and hands back one of its own pages with the token attached, so the
 * preview is the real page: scripts run, the site's CSS loads, the chrome is the
 * chrome. The fragment this used to return needed the site's assets rewritten,
 * its anti-flash gate defeated and its noscript blocks stripped to look like a
 * page — and still was not one.
 *
 * NOTHING IS PERSISTED: no layout row is created, no version is written, and the
 * active override for the path is untouched.
 */
export const CONTENT_LAYOUT_PREVIEW = gql`
  query ContentLayoutPreview(
    $body: Str!
    $syntax: Str
    $templatePath: Str
    $recordType: Str
    $recordId: Integer
    $previewUrl: Str
    $contentLayoutType: Str
  ) {
    contentLayoutPreview(
      body: $body
      syntax: $syntax
      templatePath: $templatePath
      recordType: $recordType
      recordId: $recordId
      previewUrl: $previewUrl
      contentLayoutType: $contentLayoutType
    ) {
      url
      token
      expiresIn
      record {
        type
        oid
        title
        path
      }
    }
  }
`;
